import Button from '@passkeys/ui/button';
import Callout from '@passkeys/ui/callout';
import { useCallback, useEffect } from 'react';

import type { Asset } from '@/types';
import exodus from '@/ui/exodus';
import useReceiveAddress from '@/ui/hooks/use-receive-address.js';
import showBuyWidgetModal from '@/ui/modals/fiat-buy-widget-modal/index.js';
import showUnavailableModal from '@/ui/modals/fiat-unavailable-modal/index.js';
import showReceiveStartedModal from '@/ui/modals/receive-started-modal/index.js';
import type { ReceiveStartedModalAction } from '@/ui/modals/receive-started-modal/index.js';
import showTokenPickerModal from '@/ui/modals/token-picker-modal';
import useBuyFormValidation from './hooks/use-buy-form-validation.js';
import useBuyPair from './hooks/use-buy-pair.js';
import { useBuyQuote } from './hooks/use-buy-quote.js';
import CryptoForm from './parts/crypto-form.js';
import FiatForm from './parts/fiat-form.js';

const DEFAULT_TOKEN_NAME = 'bitcoin';

export type BuyFormData = {
  fiatAsset: Asset;
  fiatAmount: number;
  cryptoAsset: Asset;
  cryptoAmount: number;
};

type BuyFormProps = {
  variant: 'fiat' | 'crypto';
  initialToAssetName?: string;
  referrer?: string;
  submitButtonClassName?: string;
  primarySuccessAction: ReceiveStartedModalAction;
  alternativeSuccessAction: ReceiveStartedModalAction;
};

function BuyForm({
  variant,
  initialToAssetName = DEFAULT_TOKEN_NAME,
  referrer = '',
  submitButtonClassName,
  primarySuccessAction,
  alternativeSuccessAction,
}: BuyFormProps) {
  const {
    providerName,
    fromAsset: fiatAsset,
    fromAmount: fiatAmount,
    setFromAmount: setFiatAmount,
    toAsset: cryptoAsset,
    setToAsset,
    toAmount: cryptoAmount,
    setToAmount: setCryptoAmount,
    toAssets: cryptoAssets,
  } = useBuyPair({ initialToAssetName });

  const { address } = useReceiveAddress({ assetName: cryptoAsset.name });
  const { value: quote, loading } = useBuyQuote({
    providerName,
    fiatAsset,
    fiatAmount: fiatAmount.toDefaultNumber(),
    cryptoAsset,
  });

  const { hasError, failedValidation } = useBuyFormValidation({
    amount: fiatAmount.toDefaultNumber(),
    quote,
    loadingQuote: loading,
  });

  const handleTokenSelect = async () => {
    const { result } = await showTokenPickerModal({
      assets: cryptoAssets,
    });

    if (!result) {
      return;
    }

    setToAsset(result.name);
  };

  const handleContinueClick = useCallback(async () => {
    if (!cryptoAmount) {
      return;
    }

    exodus.analytics.track({
      event: 'OnRampConfigureAssetPurchase',
      properties: {
        origin: referrer,
      },
    });

    const fiatAmountNumber = fiatAmount.toDefaultNumber();
    const cryptoAmountNumber = cryptoAmount.toDefaultNumber();

    const { id: orderId } = await exodus.fiatRamp.createOrder({
      fromAmount: fiatAmountNumber,
      fromAsset: fiatAsset.name,
      fromAssetName: fiatAsset.name,
      provider: providerName,
      toAsset: cryptoAsset.displayTicker,
      toAssetName: cryptoAsset.name,
      toAmount: cryptoAmountNumber,
      totalFee: 1,
      userAddress: address,
    });

    showBuyWidgetModal({
      cryptoAsset,
      fiatAsset,
      fiatAmount: fiatAmountNumber,
      orderId,
      onOrderCreated: () => {
        showReceiveStartedModal({
          amount: cryptoAmountNumber,
          assetName: cryptoAsset.name,
          type: 'buy',
          primaryAction: primarySuccessAction,
          alternativeAction: alternativeSuccessAction,
        });

        exodus.analytics.track({
          event: 'OnRampPurchaseConfirm',
          properties: {
            origin: referrer,
            success: true,
          },
        });
      },
      onUnavailable: () => {
        showUnavailableModal();
      },
    });
  }, [
    address,
    cryptoAmount,
    referrer,
    fiatAmount,
    providerName,
    fiatAsset,
    cryptoAsset,
    primarySuccessAction,
    alternativeSuccessAction,
  ]);

  useEffect(() => {
    if (loading || !quote) {
      return;
    }

    setCryptoAmount(cryptoAsset.currency.defaultUnit(quote.to.amount));
  }, [cryptoAsset.currency, loading, quote, setCryptoAmount]);

  return (
    <>
      {variant === 'fiat' && (
        <FiatForm
          cryptoAsset={cryptoAsset}
          fiatAmount={fiatAmount}
          cryptoAmount={cryptoAmount}
          quote={quote}
          loading={loading}
          onTokenSelect={handleTokenSelect}
          onAmountChange={setFiatAmount}
        />
      )}

      {variant === 'crypto' && (
        <CryptoForm
          fiatAsset={fiatAsset}
          cryptoAsset={cryptoAsset}
          fiatAmount={fiatAmount}
          cryptoAmount={cryptoAmount}
          quote={quote}
          loading={loading}
          onAmountChange={setFiatAmount}
        />
      )}

      {hasError ? (
        <Callout type="error">{failedValidation!.message}</Callout>
      ) : (
        <Button
          disabled={!loading && !fiatAmount}
          onClick={handleContinueClick}
          loading={loading}
          className={submitButtonClassName}
          size="large"
          dataTestId="buy-form-continue-button"
        >
          Continue
        </Button>
      )}
    </>
  );
}

export default BuyForm;
